import React, { useState, useEffect } from 'react';
import { Gallery, livingRoomProducts } from './Gallery';
import { CuratedCollections, livingRoomCollections } from './CuratedCollections';
import { ProductShowcase, livingRoomShowcaseItems } from './productShowCase';
import ScrollingSection from './ScrollingSection';
import ScrollToTop from './ScroollToTop';
import Contact from './Contact';

const livingRoomCategories = ['Sofas', 'Armchairs', 'Coffee Tables', 'TV Units'];

// Contact Modal Component
const ContactModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white shadow-3xl w-full max-w-2xl max-h-[90vh] sm:max-h-[95vh] overflow-y-auto relative rounded-lg">
        <button
          className="absolute top-2 right-3 sm:top-3 sm:right-4 text-gray-500 hover:text-gray-700 text-2xl sm:text-3xl font-bold z-[9999]"
          aria-label="Close modal"
          onClick={onClose}
        >
          &times;
        </button>
        <Contact />
      </div>
    </div>
  );
};

const LivingRoom = () => {
  const [showModal, setShowModal] = useState(false);

  // Preload hero image
  useEffect(() => {
    const img = new Image();
    img.src = "https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?auto=compress&cs=tinysrgb&w=1920";
  }, []);

  const openModal = () => setShowModal(true);
  const closeModal = () => setShowModal(false);

  return (
    <div className="animate-slideUp">
      <ScrollToTop />
      {/* Hero Section */}
      <header
        className="relative bg-cover bg-center h-[50vh] sm:h-[60vh] md:h-[70vh] lg:h-[80vh]"
        style={{
          backgroundImage: "url('https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?auto=compress&cs=tinysrgb&w=1920')",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/10 to-transparent"></div>
        <div className="absolute inset-0 flex items-center px-4 sm:px-6 md:px-8 lg:px-16 z-10">
          <div className="w-full md:w-2/3 lg:w-1/2 text-white">
            <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-tight mb-4">
              Timeless Elegance For Modern Living
            </h1>
            <p className="font-heading text-sm sm:text-base md:text-lg lg:text-xl mb-6">
              Transform your living room into a sanctuary of comfort and style
              with our premium furniture collection designed for relaxation
              and rejuvenation.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-3 px-6 sm:py-4 sm:px-8 text-sm sm:text-base rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg">
                Explore the Collection
              </button>
              <button onClick={openModal} className="border-2 border-white hover:bg-white hover:text-gray-900 font-semibold py-3 px-6 sm:py-4 sm:px-8 text-sm sm:text-base rounded-lg transition-all duration-300 transform hover:scale-105">
                Book Consultation
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Product Showcase */}
      <ProductShowcase
        title="Living Room Furniture Collections"
        items={livingRoomShowcaseItems}
      />

      {/* Signature Collection */}
      <section className="bg-gray-300 py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-center text-2xl sm:text-3xl font-semibold mb-6 sm:mb-8">Signature Collection</h2>
          <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-2xl shadow-md flex flex-col lg:flex-row items-center gap-6 lg:gap-8">
            <div className="w-full lg:w-1/3 relative overflow-hidden bg-gray-100 rounded-lg shadow-md">
              <div className="aspect-[4/5] w-full">
                <img
                  src="https://images.pexels.com/photos/16261824/pexels-photo-16261824/free-photo-of-modern-design-of-room.jpeg?auto=compress&cs=tinysrgb&w=800&q=80"
                  alt="The Artisan Chair"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => { 
                    e.target.onerror = null; 
                    e.target.src = "https://placehold.co/600x750/E5E7EB/6B7280?text=Modern+Design"; 
                  }}
                />
              </div>
            </div>
            <div className="w-full lg:w-2/3">
              <h3 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4">Modern Design Of Livingroom</h3>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4 sm:mb-6">
                Our flagship dining chair combines traditional craftsmanship with modern design sensibilities. Handcrafted from sustainably sourced oak with premium upholstery,
                it offers exceptional comfort for extended dining experiences while maintaining a sophisticated profile that elevates any restaurant interior.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-4 sm:mb-6">
                <button className="bg-yellow-700 hover:bg-yellow-800 text-white py-2 px-4 sm:py-3 sm:px-6 text-sm sm:text-base rounded transition-colors">
                  View Details
                </button>
                <button onClick={openModal} className="bg-gray-700 hover:bg-gray-800 text-white py-2 px-4 sm:py-3 sm:px-6 text-sm sm:text-base rounded transition-colors">
                  Request a Quote
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm">
                <span className="text-gray-700 font-semibold">5+ Finishes</span>
                <span className="text-gray-700 font-semibold">10-Year Warranty</span>
                <span className="text-gray-700 font-semibold">100% Sustainable</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="bg-white py-8 sm:py-12 min-h-[600px] sm:min-h-[800px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-semibold mb-6 sm:mb-8 text-center">Living Room Gallery</h2>
          <div className="min-h-[500px] sm:min-h-[600px]">
            <Gallery
              products={livingRoomProducts}
              categories={livingRoomCategories}
              autoCycle={true}
              cycleInterval={2000}
            />
          </div>
        </div>
      </section>

      {/* Scrolling Section */}
      <section>
        <ScrollingSection
          sectionContent={
            <section className="relative z-10 bg-black/80 p-4 sm:p-6 md:p-8 lg:p-12 xl:p-16 w-full min-h-screen flex items-center justify-center">
              <div className="max-w-6xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center">
                  <div className="space-y-4 sm:space-y-6 text-left">
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif text-white leading-tight">Elegant Livingroom Setup</h2>
                    <p className="text-gray-300 text-sm sm:text-base md:text-lg leading-relaxed">
                      Transform your Livingroom into a cozy retreat with our premium furniture collection. Featuring sleek designs and high-quality materials, each piece is crafted for comfort and style, ensuring a serene and luxurious personal space.
                    </p>
                    <button className="mt-4 sm:mt-8 inline-block bg-gray-600 text-white px-6 py-2.5 sm:px-8 sm:py-3 text-sm sm:text-base md:text-lg rounded-full font-semibold hover:bg-gray-700 transition-colors duration-300 shadow-lg hover:shadow-xl">
                      Explore More
                    </button>
                  </div>
                  <div>
                    <img
                      src="/assets/slide1.png"
                      alt="Modern Living Room"
                      className="rounded-xl shadow-lg w-full h-auto object-cover transform hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                </div>
              </div>
            </section>
          }
          imageUrl="/assets/slide2.png"
        />
      </section>

      {/* Curated Collections */}
      <CuratedCollections
        title="Curated Living Room Styles"
        description="Discover cohesive furniture sets and decor ideas for your home's heart."
        collections={livingRoomCollections}
      />

      {/* Contact Modal */}
      <ContactModal isOpen={showModal} onClose={closeModal} />
    </div>
  );
};

export default LivingRoom;